import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { ButtonComponent } from "@components/button/button.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { InputComponent } from "@components/input/input.component";
import { PageComponent } from "@components/page/page.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { SubmitButtonComponent } from "@components/submit-button/submit-button.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable, of } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { ITypeAheadDTO } from "~app/common/models/common/ITypeAheadDTO";
import { KundorderTyp } from "~app/common/models/constants";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { EventService } from "~app/common/services/cqs/event.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { StatedataService, Volatile } from "~app/common/services/statedata.service";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { HandelssortimentTreeType, KundorderListRequestDTO, NamnIdDTO } from "~app/features/forsaljning/models/KundorderListRequestDTO";
import { Kundorder } from "~app/features/forsaljning/models/KundorderListResponseDTO";
import { KundorderService } from "../kundorder//kundorder.service";
import { CreateKundorderModalComponent } from "../kundorder/create-kundorder-modal/create-kundorder-modal.component";
import { KundorderCreatedMessage } from "../kundorder/events/KundorderCreatedMessage";
import { TerminalorderListComponent } from "./terminalorder-list/terminalorder-list.component";
import { TerminalorderService } from "./terminalorder.service";

@Component({
    selector: "rask-terminalorder",
    templateUrl: "./terminalorder.component.html",
    styleUrls: ["./terminalorder.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        FormsModule,
        ReactiveFormsModule,
        SelectorComponent,
        InputComponent,
        DatepickerComponent,
        TreeSelectorComponent,
        SubmitButtonComponent,
        ButtonComponent,
        TerminalorderListComponent,
        TranslatePipe
    ]
})
export class TerminalorderComponent implements OnInit {
    avtalsansvarig: BehaviorSubject<ITypeAheadResult> = new BehaviorSubject<ITypeAheadResult>(null);
    form: UntypedFormGroup;
    handelssortiment: SelectionItem[];
    request: KundorderListRequestDTO;
    terminalordrar: Kundorder[];
    volatile: Volatile;

    private readonly WIK = "terminalorder.search";
    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly eventService = inject(EventService);
    private readonly formBinder = inject(FormBinderService);
    private readonly kundorderService = inject(KundorderService);
    private readonly modalService = inject(RaskModalService);
    private readonly router = inject(Router);
    private readonly stateDataService = inject(StatedataService);
    private readonly terminalorderService = inject(TerminalorderService);
    private readonly translateService = inject(TranslateService);
    private readonly webinstallningarService = inject(WebinstallningarService);

    constructor() {
        this.volatile = this.stateDataService.getVolatile();
    }

    avtalAnsvarigSearch = (input: string) => this.kundorderService.searchForAvtalsansvarigTypeahead(input);

    canEdit(): boolean {
        return this.authenticationService.canEdit();
    }

    clear() {
        this.request = new KundorderListRequestDTO();
        this.form.get("kund").setValue(new NamnIdDTO());
        this.form.get("beskrivning").setValue(null);
        this.form.get("periodFrom").setValue(null);
        this.form.get("periodTom").setValue(null);
        this.form.get("avtalsansvarig").setValue({ kod: null,
            namn: null });
        this.form.get("handelssortiment").setValue([]);
        this.form.get("mottagningsplats").setValue(new NamnIdDTO());
        this.form.get("ordernummer").setValue(null);
        this.setupHandelssortimentTreeSelector([]).subscribe();
        this.request.avtalsansvarig = { kod: null,
            namn: null } as ITypeAheadDTO;
    }

    create = (): void => {
        this.modalService.open(
            this.translateService.instant("TERMINALORDER_DETAIL_BUTTON_SKAPA_TERMINALORDER"),
            CreateKundorderModalComponent, "xl", true, KundorderTyp.Terminalorder);
    };

    mottagningsplatsSearch = (input: string) => this.terminalorderService.getTerminalTypeAhead(input);

    ngOnInit() {
        this.request = this.volatile.get<KundorderListRequestDTO>(this.WIK);
        if (this.request) {
            this.setupHandelssortimentTreeSelector(this.request.handelssortiment)
                .subscribe(() => {
                    this.form = this.formBinder.CreateGroup(this.mapToDto(this.request), null, null, false);
                    this.search(false);
                });
            return;
        }

        this.request = this.webinstallningarService.get<KundorderListRequestDTO>(this.WIK);
        if (this.request == null) {
            this.request = new KundorderListRequestDTO();
        }

        this.setupHandelssortimentTreeSelector(this.request.handelssortiment).subscribe(() => {
            this.form = this.formBinder.CreateGroup(this.mapToDto(this.request), null, null, false);
        });

        this.eventService.subscribeEvent<KundorderCreatedMessage>(KundorderCreatedMessage.$type).subscribe(k => {
            this.router.navigate([
                "terminalorder",
                k.kundorderId
            ]);
        });
    }

    search(saveState = true) {
        const searchParams = this.mapToDto(this.form.value as KundorderListRequestDTO);
        searchParams.handelssortiment = this.terminalorderService.getSelectedHandelssortiment(this.handelssortiment);
        this.busyService.addWork(this.terminalorderService.search(searchParams)).subscribe(result => {
            this.terminalordrar = (result as any).kundorderList;
            if (saveState) {
                this.volatile.set(this.WIK, searchParams);
                this.webinstallningarService.set<KundorderListRequestDTO>(this.WIK, searchParams);
            }
        });
    }

    set = (val: any) => of([val]);

    typeAheadValueFormatter = (item: ITypeAheadResult) => item;

    viewFormatter = (item: any) => item?.namn ?? item?.name;

    private mapToDto(values: KundorderListRequestDTO): KundorderListRequestDTO {
        const dto = new KundorderListRequestDTO();
        dto.avtalsansvarig = values.avtalsansvarig ?? { kod: null,
            namn: null } as ITypeAheadDTO;
        dto.beskrivning = values.beskrivning ?? "";
        dto.handelssortiment = values.handelssortiment ?? [];
        dto.kund = values.kund ?? new NamnIdDTO();
        dto.mottagningsplats = values.mottagningsplats ?? new NamnIdDTO();
        dto.periodFrom = DateUtils.nullableDate(values.periodFrom);
        dto.periodTom = DateUtils.nullableDate(values.periodTom);
        dto.ordernummer = values.ordernummer ?? "";
        return dto;
    }

    private setupHandelssortimentTreeSelector(valdaHandelssortiment: HandelssortimentTreeType[]): Observable<any> {
        return new Observable<any>(cb => {
            this.terminalorderService.getTerminalorderBasData()
                .subscribe(p => {
                    this.handelssortiment = this.terminalorderService.setupHandelssortimentTreeSelector(p.handelssortiment, valdaHandelssortiment);
                    cb.next(null);
                });
        });
    }
}
